import React, { useRef, useState, useEffect } from "react";
import ct from "class-types.macro";
import { useVirtual } from "react-virtual";
import { useCombobox } from "downshift";
import StandardIcon from "~/common/Icons/Standard";
import Icon from "~/common/Icons/Critical/";

interface SearchableSelectProps {
  options: string[];
  placeholder: string;
  label: string;
  defaultOption?: string;
  handleChange?: (inputVal: string) => void;
  disabled?: boolean;
  handleClear?: () => void;
  isFilterRemoved?: boolean;
}

const SearchableSelect = ({
  options,
  placeholder,
  label,
  handleChange,
  disabled,
  handleClear,
  defaultOption,
  isFilterRemoved,
}: SearchableSelectProps) => {
  const getItems = (search: string) =>
    options.filter((n) => n.toLowerCase().includes(search.toLowerCase()));

  const [inputValue, setInputValue] = useState(defaultOption || "");

  const [completed, setCompleted] = useState(false);
  const items = getItems(inputValue);
  const listRef = useRef<HTMLElement>(null);
  const rowVirtualizer = useVirtual({
    size: items.length,
    parentRef: listRef,
    estimateSize: React.useCallback(() => 30, []),
    overscan: 2,
  });

  const {
    isOpen,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    highlightedIndex,
    getItemProps,
    openMenu,
    selectedItem,
  } = useCombobox({
    items,
    inputValue,
    onInputValueChange: ({ inputValue: newInputValue }) => {
      setInputValue(newInputValue || "");
      if (handleChange) {
        handleChange(newInputValue || "");
      }
    },

    scrollIntoView: () => {},
    onHighlightedIndexChange: ({ highlightedIndex: highlightedIndexChange }) =>
      rowVirtualizer.scrollToIndex(highlightedIndexChange || 0),
  });
  const size = rowVirtualizer.totalSize;

  useEffect(() => {
    if (isFilterRemoved) {
      // reset the input value for model selection
      setInputValue("");
      if (handleChange) {
        handleChange("");
      }
    }
  }, [isFilterRemoved, handleChange]);

  const disabledStyles = disabled
    ? {
        background: "#FBFBFB",
        color: "rgba(59, 61, 59, 0.5)",
        opacity: "0.5",
        textShadow: " 0px 1px 0px #FFFFFF",
        boxShadow: "inset 0px 1px 1px rgba(0, 0, 0, 0.13)",
        cursor: "auto",
      }
    : null;

  return (
    <div className={ct("mx-auto", "mb-2", "relative")}>
      <label
        {...getLabelProps()}
        className={ct("font-normal", "text-sm", "mb-1", "text-gray-800")}
      >
        {label}
      </label>
      <div className={ct("flex")} {...getComboboxProps()}>
        <input
          {...getInputProps({
            onFocus: () => {
              if (!isOpen && inputValue.length === 0) {
                openMenu();
                setCompleted(false);
              }
            },
            onChange: () => {
              setCompleted(false);
            },
            onBlur: () => {
              if (inputValue.length > 0) {
                setCompleted(true);
              }
            },
          })}
          style={disabledStyles}
          className={`${ct(
            "rounded-l",
            "border-solid",
            "border",
            "border-r-0",
            "box-border",
            "inline-block",
            "flex-1",
            "text-sm",
            "leading-4",
            "m-0",
            "focus:outline-none",
            "py-2",
            "pl-2",
            "text-gray-800",
            "align-middle",
            "w-32",
            "transition",
            "ease-linear",
            "duration-200",
            "lg:w-full",
          )}
          ${
            isOpen
              ? ct("shadow-inner-blue", "border-color-blue-80")
              : ct("border-gray-200", "shadow-inner-white-13")
          }  ${completed ? ct("bg-gray-200") : ct("bg-white")} `}
          placeholder={placeholder}
          disabled={disabled}
        />
        {inputValue &&
          inputValue.length > 0 &&
          (selectedItem || defaultOption) && (
            <button
              tabIndex={-1}
              onClick={() => {
                setInputValue("");
                if (handleChange) {
                  handleChange("");
                }
                if (handleClear) {
                  handleClear();
                }
                setCompleted(false);
              }}
              aria-label="clear selection"
              className={`${ct(
                "border-gray-200",
                "rounded-r",
                "border-solid",
                "border-l-0",
                "border-t",
                "border-r",
                "border-b",
                "cursor-pointer",
                "flex",
                "items-center",
                "justify-center",
                "m-0",
                "p-2",
                "relative",
                "shadow-inner-white-13",
              )}
            ${
              isOpen
                ? ct("shadow-inner-blue", "border-color-blue-80")
                : ct("border-gray-200", "shadow-inner-white-13")
            }
            ${completed ? ct("bg-gray-200") : ct("bg-white")}`}
              type="button"
            >
              <span
                style={{ left: "-5px" }}
                className={`${ct(
                  "block",
                  "h-full",
                  "absolute",
                  "w-2",
                  "shadow-inner-white-13",
                )}
              ${completed ? ct("hidden") : "bg-white"} `}
              />
              <StandardIcon name="cancel" className={ct("h-3", "w-3")} />
            </button>
          )}
        {((!selectedItem && !defaultOption) || inputValue.length === 0) && (
          <button
            type="button"
            {...getToggleButtonProps()}
            aria-label="toggle menu"
            className={`${ct(
              "bg-white",
              "rounded-r",
              "border-solid",
              "border-l-0",
              "border-t",
              "border-r",
              "border-b",
              "cursor-pointer",
              "flex",
              "items-center",
              "justify-center",
              "m-0",
              "p-2",
              "relative",
            )} ${
              isOpen
                ? ct("shadow-inner-blue", "border-color-blue-80")
                : ct("border-gray-200", "shadow-inner-white-13")
            }`}
            disabled={disabled}
            style={disabledStyles}
          >
            {!disabled && (
              <span
                style={{ left: "-5px" }}
                className={ct(
                  "bg-white",
                  "block",
                  "h-full",
                  "absolute",
                  "w-2",
                  "shadow-inner-white-13",
                )}
              />
            )}
            <Icon name="down-micro" className={ct("h-3", "w-3")} />
          </button>
        )}
      </div>

      <ul
        className={`${ct(
          "bg-white",
          "border-transparent",
          "rounded",
          "list-none",
          "m-0",
          "overflow-y-auto",
          "overflow-x-hidden",
          "p-0",
          "absolute",
          "w-full",
          "z-20",
          "shadow-white-02",
        )} ${
          isOpen
            ? ct("border-gray-200", "border-solid", "border", "max-h-40")
            : ct("hidden", "max-h-none")
        } `}
        style={{ top: 60, background: "#FBFBFB", height: `200px` }}
        {...getMenuProps({ ref: listRef })}
      >
        {isOpen && (
          <>
            <li style={{ height: `${size}px` }} />
            {rowVirtualizer.virtualItems.map((virtualRow) => (
              <li
                key={items[virtualRow.index]}
                className={ct(
                  "cursor-pointer",
                  "flex",
                  "items-center",
                  "text-sm",
                  "pl-2",
                  "absolute",
                  "top-0",
                  "left-0",
                  "w-full",
                )}
                {...getItemProps({
                  index: virtualRow.index,
                  item: items[virtualRow.index] || "",
                  style: {
                    backgroundColor:
                      highlightedIndex === virtualRow.index
                        ? "#33bef2"
                        : "inherit",
                    height: virtualRow.size,
                    transform: `translateY(${virtualRow.start + 5}px)`,
                    textShadow:
                      highlightedIndex === virtualRow.index
                        ? "0px 1px 1px rgba(0, 0, 0, 0.25)"
                        : "",
                    color:
                      highlightedIndex === virtualRow.index
                        ? "#fff"
                        : "inherit",
                  },
                })}
              >
                {items[virtualRow.index]}
              </li>
            ))}
          </>
        )}
      </ul>
    </div>
  );
};

export default SearchableSelect;
